import { SCORING } from "../config/gameplay";
import { clamp } from "../simulation/geometry";

export interface ScoreSnapshot {
  readonly landings: number;
  readonly departures: number;
  readonly durationSeconds: number;
  /** Completed flights per minute. */
  readonly pace: number;
  /** Landings in a row without a refused drop or a near miss. */
  readonly streak: number;
  /** Longest such run this game. */
  readonly bestStreak: number;
  /** The number the board ranks on. Never negative. */
  readonly score: number;
}

/** What a landing was worth, and why. */
export interface LandingContext {
  /**
   * Spawn weight of the airframe, and the weight of the commonest body it
   * could have been. Rarity is expressed as weights in `Airframes.ts`, so the
   * scoring reads them rather than restating which body is rare.
   */
  readonly airframeWeight: number;
  readonly commonestWeight: number;
  /**
   * Aircraft still flying at the moment of touchdown.
   *
   * The same count the HUD shows, and it excludes this one — its state is
   * already `landing` by the time the credit is taken. A player can therefore
   * read the multiplier off the screen, which is worth more than counting a
   * climbing departure as traffic would be.
   */
  readonly airborne: number;
  /** Seconds since the aircraft appeared. */
  readonly ageSeconds: number;
}

export interface Award {
  /** Running count of landings or departures, whichever was credited. */
  readonly total: number;
  /** Points this one earned, already rounded. */
  readonly points: number;
}

function trafficMultiplier(airborne: number): number {
  return (
    1 +
    Math.min(Math.max(0, airborne), SCORING.trafficCap) * SCORING.trafficStep
  );
}

/**
 * Streak multiplier for the landing that *makes* the streak `length` long, so
 * the first landing of a run is worth its base value and not a bonus.
 */
function streakMultiplier(length: number): number {
  return (
    1 +
    Math.min(Math.max(0, length - 1), SCORING.streakCap) * SCORING.streakStep
  );
}

function rarityMultiplier(weight: number, commonestWeight: number): number {
  if (!(weight > 0) || !(commonestWeight > 0)) {
    return 1;
  }
  const share = Math.min(1, weight / commonestWeight);
  return 1 + (1 - share) * SCORING.rarityBonus;
}

/** Full up to `promptSeconds`, nothing from `lazySeconds` on. */
function promptBonus(ageSeconds: number): number {
  const span = SCORING.lazySeconds - SCORING.promptSeconds;
  if (span <= 0) {
    return SCORING.promptBonus;
  }
  const decay = clamp((SCORING.lazySeconds - ageSeconds) / span, 0, 1);
  return SCORING.promptBonus * decay;
}

export class ScoreSystem {
  private landings = 0;
  private departures = 0;
  private currentStreak = 0;
  private longestStreak = 0;
  private points = 0;

  reset(): void {
    this.landings = 0;
    this.departures = 0;
    this.currentStreak = 0;
    this.longestStreak = 0;
    this.points = 0;
  }

  get streak(): number {
    return this.currentStreak;
  }

  get score(): number {
    return this.points;
  }

  creditLanding(context: LandingContext): Award {
    this.landings += 1;
    this.currentStreak += 1;
    this.longestStreak = Math.max(this.longestStreak, this.currentStreak);

    const earned = Math.round(
      SCORING.landing *
        rarityMultiplier(context.airframeWeight, context.commonestWeight) *
        trafficMultiplier(context.airborne) *
        streakMultiplier(this.currentStreak) +
        promptBonus(context.ageSeconds),
    );
    this.points += earned;
    return { total: this.landings, points: earned };
  }

  /**
   * A departure is worth the traffic it was flown into and nothing else: the
   * player chose when to release it, but not what it was or how long it sat.
   */
  creditDeparture(airborne: number): Award {
    this.departures += 1;
    const earned = Math.round(SCORING.departure * trafficMultiplier(airborne));
    this.points += earned;
    return { total: this.departures, points: earned };
  }

  /**
   * Charge for a lost separation.
   *
   * Floored at zero rather than allowed to go negative: a run that ends below
   * nothing is indistinguishable from a run nobody played, and the board would
   * have to rank it somewhere.
   */
  penalise(points: number): number {
    const charged = Math.min(this.points, Math.max(0, Math.round(points)));
    this.points -= charged;
    return charged;
  }

  /**
   * End the current run of clean landings.
   *
   * Returns the length that was lost, so the caller can stay quiet when there
   * was nothing to break.
   */
  breakStreak(): number {
    const lost = this.currentStreak;
    this.currentStreak = 0;
    return lost;
  }

  snapshot(durationSeconds: number): ScoreSnapshot {
    return Object.freeze({
      landings: this.landings,
      departures: this.departures,
      durationSeconds,
      pace: paceFor(this.landings + this.departures, durationSeconds),
      streak: this.currentStreak,
      bestStreak: this.longestStreak,
      score: this.points,
    });
  }
}

export function paceFor(
  completedFlights: number,
  durationSeconds: number,
): number {
  return durationSeconds > 0 ? (completedFlights * 60) / durationSeconds : 0;
}
